// ===========================================
// COMPONENTE: MarcarPresentadoDialog
// ===========================================
// Dialog para marcar un vencimiento como presentado
// Migrado a TanStack Form

import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { CheckCircle, Loader2, Plus, X, Upload } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { toast } from 'sonner'
import { formatPeriodo } from '@/lib/formatPeriodo'
import {
  useMarcarPresentadoForm,
  getFieldError,
  type MarcarPresentadoAllValues,
} from './forms'
import {
  CATEGORIAS_PRESENTACION,
  CATEGORIAS_DOCUMENTO,
  esArchivoPermitido,
  esTamanoPermitido,
  LIMITE_ARCHIVO_MB,
} from '@/core/types/documentos'
import { useObligacionesRecurrentes } from '@/hooks/useObligacionesRecurrentes'
import { formatCurrency } from '@/lib/utils'
import {
  generarMensajeRecategorizacion,
  CATEGORIAS_MONOTRIBUTO,
  type CategoriaMonotributo,
  type RegimenRecategorizacion,
} from './mensajeRecategorizacion'

// Tipo para el vencimiento (compatible con el existente)
interface VencimientoParaMarcar {
  id: string
  tipo_impuesto: string
  cliente_nombre?: string
  periodo: string
  fecha_vencimiento: string
  origen_tipo?: string | null
  /**
   * ORR-238: ancla del vencimiento a su obligación recurrente. La vista lo expone
   * desde ORR-230 y es lo que permite saber si esta presentación es una
   * recategorización, sin depender del nombre (editable) ni de `tipo_obligacion`
   * (llega NULL en todos los recurrentes).
   */
  obligacion_recurrente_id?: string | null
}

// Tipo para adjunto local
interface AdjuntoLocal {
  id: string
  file: File | null
  categoria: string
  descripcion: string
}

// Tipo para los datos de confirmacion
export interface MarcarPresentadoData {
  fecha_presentacion: string
  numero_presentacion?: string
  monto_determinado?: number
  forma_pago?: 'VEP' | 'debito_automatico' | 'efectivo'
  observaciones?: string
  adjuntos?: Array<{ file: File; categoria: string; descripcion?: string }>
  mensaje_libre?: string
  /** ORR-323: solo en recategorización. Se escribe en la faceta del cliente al presentar. */
  categoria_monotributo?: CategoriaMonotributo
}

const MAX_ADJUNTOS = 10

interface MarcarPresentadoDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  vencimiento: VencimientoParaMarcar | null
  onConfirmar: (id: string, datos: MarcarPresentadoData) => Promise<void>
}

export function MarcarPresentadoDialog({
  open,
  onOpenChange,
  vencimiento,
  onConfirmar,
}: MarcarPresentadoDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [adjuntos, setAdjuntos] = useState<AdjuntoLocal[]>([])

  const esFiscal = vencimiento?.origen_tipo === 'fiscal'
  const esMonotributo = esFiscal && vencimiento?.tipo_impuesto === 'Monotributo'

  // ORR-238: el tipo de presentación se resuelve contra la obligación recurrente,
  // que es donde vive el campo declarativo. Son 4 obligaciones y la query está
  // cacheada por TanStack Query, así que el cruce sale gratis.
  const { data: obligaciones } = useObligacionesRecurrentes()
  const esRecategorizacion =
    !esFiscal &&
    obligaciones?.find((o) => o.id === vencimiento?.obligacion_recurrente_id)
      ?.tipo_presentacion === 'recategorizacion'

  const handleSubmit = async (value: MarcarPresentadoAllValues) => {
    if (!vencimiento) return

    // ORR-176: con débito automático (solo Monotributo) no hay comprobante que
    // adjuntar → el adjunto deja de ser obligatorio.
    const exigeAdjunto = esFiscal && value.forma_pago !== 'debito_automatico'
    const adjuntosConArchivo = adjuntos.filter((a) => a.file !== null)
    if (exigeAdjunto && adjuntosConArchivo.length === 0) {
      toast.error('Se requiere al menos un adjunto (DDJJ u otro comprobante)')
      return
    }
    // Categoría requerida para todo adjunto cargado (fiscal o manual)
    if (adjuntos.some((a) => a.file !== null && a.categoria.trim() === '')) {
      toast.error('Seleccioná una categoría para todos los adjuntos')
      return
    }

    // Validate descripcion when categoria === 'otros'
    if (adjuntos.some((a) => a.file !== null && a.categoria === 'otros' && a.descripcion.trim() === '')) {
      toast.error('Indicá el tipo de archivo para los adjuntos con categoría "Otros"')
      return
    }

    setIsSubmitting(true)
    try {
      // Calculate monto with sign based on saldo_tipo (fiscal only).
      // Monotributo no renderiza saldo_tipo: su default 'a_pagar' deja el monto
      // positivo (cuota a pagar), sin pasar por la rama a_favor/sin_movimiento.
      let montoFinal: number | undefined
      if (esFiscal) {
        if (value.saldo_tipo === 'sin_movimiento') {
          montoFinal = 0
        } else if (value.monto_determinado != null) {
          montoFinal = value.saldo_tipo === 'a_favor' && value.monto_determinado > 0
            ? -value.monto_determinado
            : value.monto_determinado
        }
      } else if (esRecategorizacion && value.monto_determinado != null) {
        // ORR-238: la cuota nueva es un monto a pagar, sin la rama de saldo a favor
        // (una recategorización no genera saldo). Se persiste en `monto_determinado`,
        // que ya existía y hasta ahora solo se llenaba en la rama fiscal.
        montoFinal = value.monto_determinado
      }

      // ORR-238: el mensaje se arma acá y viaja por `mensaje_libre`, al que
      // `generarMensajeDefault` (EnviarPresentadoEmailDialog) le da precedencia 1.
      // Por eso no hay que tocar el servicio de email ni el template.
      const mensajeRecategorizacion =
        esRecategorizacion && value.categoria_monotributo && value.regimen_recategorizacion
          ? generarMensajeRecategorizacion({
              categoria: value.categoria_monotributo,
              regimen: value.regimen_recategorizacion,
              monto: formatCurrency(value.monto_determinado ?? 0),
            })
          : undefined

      // Filter adjuntos that have a file selected and map to wire shape
      const adjuntosValidos = adjuntos
        .filter((a): a is { id: string; file: File; categoria: string; descripcion: string } =>
          a.file !== null && a.categoria !== ''
        )
        .map((a) => ({
          file: a.file,
          categoria: a.categoria,
          descripcion: a.categoria === 'otros' ? a.descripcion.trim() : undefined,
        }))

      await onConfirmar(vencimiento.id, {
        fecha_presentacion: value.fecha_presentacion,
        numero_presentacion: esFiscal ? (value.numero_presentacion || undefined) : undefined,
        monto_determinado: montoFinal,
        forma_pago: esMonotributo ? (value.forma_pago ?? 'VEP') : undefined,
        observaciones: value.observaciones || undefined,
        adjuntos: adjuntosValidos.length > 0 ? adjuntosValidos : undefined,
        mensaje_libre: !esFiscal
          ? (mensajeRecategorizacion ?? value.mensaje_libre ?? undefined)
          : undefined,
        // ORR-323: hasta acá la categoría solo vivía dentro del texto del mail.
        categoria_monotributo: esRecategorizacion
          ? (value.categoria_monotributo ?? undefined)
          : undefined,
      })
      form.reset()
      setAdjuntos([])
      onOpenChange(false)
    } catch (error) {
      console.error('Error al marcar como presentado:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const { form } = useMarcarPresentadoForm({
    onSubmit: handleSubmit,
    esFiscal,
    esRecategorizacion,
  })

  const handleAgregarAdjunto = () => {
    if (adjuntos.length >= MAX_ADJUNTOS) return
    setAdjuntos((prev) => [...prev, { id: crypto.randomUUID(), file: null, categoria: '', descripcion: '' }])
  }

  const handleDescripcionChange = (index: number, descripcion: string) => {
    setAdjuntos((prev) => prev.map((a, i) => i === index ? { ...a, descripcion } : a))
  }

  const handleQuitarAdjunto = (index: number) => {
    setAdjuntos((prev) => prev.filter((_, i) => i !== index))
  }

  const handleFileChange = (index: number, file: File | null) => {
    if (file) {
      if (!esArchivoPermitido(file)) {
        return
      }
      if (!esTamanoPermitido(file)) {
        return
      }
    }
    setAdjuntos((prev) => prev.map((a, i) => i === index ? { ...a, file } : a))
  }

  const handleCategoriaChange = (index: number, categoria: string) => {
    setAdjuntos((prev) => prev.map((a, i) => i === index ? { ...a, categoria } : a))
  }

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setAdjuntos([])
      form.reset()
    }
    onOpenChange(newOpen)
  }

  if (!vencimiento) return null

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            Marcar como Presentado
          </DialogTitle>
          <DialogDescription>
            {esFiscal
              ? 'Registrar la presentación de la declaración jurada'
              : 'Registrar la presentación de la obligación'}
          </DialogDescription>
        </DialogHeader>

        <div className="bg-muted/50 p-3 rounded-lg space-y-2">
          <div className="flex items-center gap-2">
            <Badge variant="outline">{vencimiento.tipo_impuesto}</Badge>
            <span className="text-sm font-medium">{vencimiento.cliente_nombre}</span>
          </div>
          <div className="text-sm text-muted-foreground">
            <span>Periodo: {formatPeriodo(vencimiento.periodo)}</span>
            <span className="mx-2">|</span>
            <span>Vencimiento: {format(parseISO(vencimiento.fecha_vencimiento), 'dd/MM/yyyy')}</span>
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            form.handleSubmit()
          }}
          className="space-y-4"
        >
          <form.Field name="fecha_presentacion">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>Fecha de Presentación *</Label>
                <Input
                  id={field.name}
                  type="date"
                  value={field.state.value || ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {getFieldError(field.state.meta.errors)}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          {/* Monotributo: Forma de pago */}
          {esMonotributo && (
            <form.Field name="forma_pago">
              {(field) => (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Forma de pago</Label>
                  <RadioGroup
                    value={field.state.value ?? 'VEP'}
                    onValueChange={(val) => field.handleChange(val as 'VEP' | 'debito_automatico' | 'efectivo')}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="VEP" id="fp_vep" />
                      <Label htmlFor="fp_vep" className="cursor-pointer font-normal">VEP</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="debito_automatico" id="fp_debito" />
                      <Label htmlFor="fp_debito" className="cursor-pointer font-normal">Déb. Automático</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="efectivo" id="fp_efectivo" />
                      <Label htmlFor="fp_efectivo" className="cursor-pointer font-normal">Efectivo</Label>
                    </div>
                  </RadioGroup>
                </div>
              )}
            </form.Field>
          )}

          {/* Fiscal: Monto Determinado */}
          {esFiscal && (
            <div className="space-y-3 p-3 border rounded-lg">
              <Label className="text-sm font-medium">Monto Determinado</Label>

              {/* Monotributo: solo monto a pagar (sin a_favor/sin_movimiento) */}
              {!esMonotributo && (
                <form.Field name="saldo_tipo">
                  {(field) => (
                    <RadioGroup
                      value={field.state.value || 'a_pagar'}
                      onValueChange={(val) => field.handleChange(val as 'a_pagar' | 'a_favor' | 'sin_movimiento')}
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="a_pagar" id="saldo_a_pagar" />
                        <Label htmlFor="saldo_a_pagar" className="cursor-pointer font-normal">
                          A pagar
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="a_favor" id="saldo_a_favor" />
                        <Label htmlFor="saldo_a_favor" className="cursor-pointer font-normal">
                          A favor
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="sin_movimiento" id="saldo_sin_movimiento" />
                        <Label htmlFor="saldo_sin_movimiento" className="cursor-pointer font-normal">
                          Sin movimiento
                        </Label>
                      </div>
                    </RadioGroup>
                  )}
                </form.Field>
              )}

              {esMonotributo ? (
                <form.Field name="monto_determinado">
                  {(field) => (
                    <div className="space-y-1">
                      <Input
                        id={field.name}
                        type="number"
                        min={0}
                        step="0.01"
                        placeholder="0.00"
                        value={field.state.value ?? ''}
                        onChange={(e) => {
                          const val = e.target.value
                          field.handleChange(val === '' ? null : parseFloat(val))
                        }}
                        onBlur={field.handleBlur}
                      />
                      {field.state.meta.errors.length > 0 && (
                        <p className="text-sm text-destructive">
                          {getFieldError(field.state.meta.errors)}
                        </p>
                      )}
                    </div>
                  )}
                </form.Field>
              ) : (
                <form.Subscribe selector={(state) => state.values.saldo_tipo}>
                  {(saldoTipo) =>
                    saldoTipo !== 'sin_movimiento' ? (
                      <form.Field name="monto_determinado">
                        {(field) => (
                          <div className="space-y-1">
                            <Input
                              id={field.name}
                              type="number"
                              min={0}
                              step="0.01"
                              placeholder="0.00"
                              value={field.state.value ?? ''}
                              onChange={(e) => {
                                const val = e.target.value
                                field.handleChange(val === '' ? null : parseFloat(val))
                              }}
                              onBlur={field.handleBlur}
                            />
                            {field.state.meta.errors.length > 0 && (
                              <p className="text-sm text-destructive">
                                {getFieldError(field.state.meta.errors)}
                              </p>
                            )}
                          </div>
                        )}
                      </form.Field>
                    ) : (
                      <div className="space-y-1">
                        <Input
                          type="number"
                          placeholder="0.00"
                          value=""
                          disabled
                        />
                        <p className="text-sm text-muted-foreground">
                          Se presentó sin movimientos. Se registrará con monto $0,00.
                        </p>
                      </div>
                    )
                  }
                </form.Subscribe>
              )}
            </div>
          )}

          {/* ORR-238 — Recategorización: en vez del mensaje libre, los datos con
              los que se arma el mensaje al cliente. */}
          {esRecategorizacion && (
            <div className="space-y-3 p-3 border rounded-lg">
              <Label className="text-sm font-medium">Datos de la recategorización</Label>

              <form.Field name="categoria_monotributo">
                {(field) => (
                  <div className="space-y-1">
                    <Label htmlFor={field.name} className="font-normal">Nueva categoría *</Label>
                    <Select
                      value={field.state.value ?? ''}
                      onValueChange={(val) => field.handleChange(val as CategoriaMonotributo)}
                    >
                      <SelectTrigger id={field.name}>
                        <SelectValue placeholder="Seleccionar categoría..." />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIAS_MONOTRIBUTO.map((cat) => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {field.state.meta.errors.length > 0 && (
                      <p className="text-sm text-destructive">
                        {getFieldError(field.state.meta.errors)}
                      </p>
                    )}
                  </div>
                )}
              </form.Field>

              <form.Field name="regimen_recategorizacion">
                {(field) => (
                  <div className="space-y-1">
                    <Label className="font-normal">Régimen *</Label>
                    <RadioGroup
                      value={field.state.value ?? 'monotributo'}
                      onValueChange={(val) => field.handleChange(val as RegimenRecategorizacion)}
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="monotributo" id="reg_monotributo" />
                        <Label htmlFor="reg_monotributo" className="cursor-pointer font-normal">
                          Monotributo
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="monotributo_unificado" id="reg_unificado" />
                        <Label htmlFor="reg_unificado" className="cursor-pointer font-normal">
                          Monotributo unificado
                        </Label>
                      </div>
                    </RadioGroup>
                    {field.state.meta.errors.length > 0 && (
                      <p className="text-sm text-destructive">
                        {getFieldError(field.state.meta.errors)}
                      </p>
                    )}
                  </div>
                )}
              </form.Field>

              <form.Field name="monto_determinado">
                {(field) => (
                  <div className="space-y-1">
                    <Label htmlFor={field.name} className="font-normal">Monto de la cuota *</Label>
                    <Input
                      id={field.name}
                      type="number"
                      min={0}
                      step="0.01"
                      placeholder="0.00"
                      value={field.state.value ?? ''}
                      onChange={(e) => {
                        const val = e.target.value
                        field.handleChange(val === '' ? null : parseFloat(val))
                      }}
                      onBlur={field.handleBlur}
                    />
                    {field.state.meta.errors.length > 0 && (
                      <p className="text-sm text-destructive">
                        {getFieldError(field.state.meta.errors)}
                      </p>
                    )}
                  </div>
                )}
              </form.Field>

              {/* Vista previa: el contador ve el texto exacto que le va a llegar al
                  cliente antes de confirmar. */}
              <form.Subscribe
                selector={(state) => [
                  state.values.categoria_monotributo,
                  state.values.regimen_recategorizacion,
                  state.values.monto_determinado,
                ] as const}
              >
                {([categoria, regimen, monto]) =>
                  categoria && regimen ? (
                    <div className="rounded-md bg-muted/50 p-3">
                      <p className="text-xs font-medium text-muted-foreground mb-1">
                        Mensaje al cliente
                      </p>
                      <p className="text-sm">
                        {generarMensajeRecategorizacion({
                          categoria,
                          regimen,
                          monto: formatCurrency(monto ?? 0),
                        })}
                      </p>
                    </div>
                  ) : null
                }
              </form.Subscribe>
            </div>
          )}

          {/* Manual: Mensaje libre para el cliente */}
          {!esFiscal && !esRecategorizacion && (
            <form.Field name="mensaje_libre">
              {(field) => (
                <div className="space-y-2">
                  <Label htmlFor={field.name}>Mensaje para el cliente *</Label>
                  <Textarea
                    id={field.name}
                    placeholder="Escribí el mensaje que se enviará al cliente..."
                    className="resize-none"
                    rows={4}
                    value={field.state.value || ''}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {getFieldError(field.state.meta.errors)}
                    </p>
                  )}
                </div>
              )}
            </form.Field>
          )}

          <form.Field name="observaciones">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>Observaciones</Label>
                <Textarea
                  id={field.name}
                  placeholder="Notas adicionales..."
                  className="resize-none"
                  rows={2}
                  value={field.state.value || ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {getFieldError(field.state.meta.errors)}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          {/* Adjuntos */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">
                Adjuntos {esFiscal ? '*' : '(opcional)'}
              </Label>
              {adjuntos.length < MAX_ADJUNTOS && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAgregarAdjunto}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Agregar adjunto
                </Button>
              )}
            </div>

            {esFiscal && adjuntos.length === 0 && (
              <p className="text-sm text-destructive/80">
                Se requiere al menos un adjunto (DDJJ u otro comprobante). Max {MAX_ADJUNTOS} archivos ({LIMITE_ARCHIVO_MB}MB c/u).
              </p>
            )}

            {!esFiscal && adjuntos.length === 0 && (
              <p className="text-sm text-muted-foreground">
                Podés adjuntar archivos opcionalmente. Max {MAX_ADJUNTOS} archivos ({LIMITE_ARCHIVO_MB}MB c/u).
              </p>
            )}

            {adjuntos.map((adjunto, index) => (
              <div key={adjunto.id} className="flex items-start gap-2 p-2 border rounded-md">
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex items-center gap-2">
                    <Label
                      htmlFor={`adjunto-file-${index}`}
                      className="flex-1 min-w-0 cursor-pointer"
                    >
                      <div className="flex items-center gap-2 border rounded-md px-3 py-2 text-sm hover:bg-muted/50 transition-colors">
                        <Upload className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span
                          className={`truncate ${adjunto.file ? 'text-foreground' : 'text-muted-foreground'}`}
                          title={adjunto.file?.name}
                        >
                          {adjunto.file ? adjunto.file.name : 'Seleccionar archivo...'}
                        </span>
                      </div>
                      <input
                        id={`adjunto-file-${index}`}
                        type="file"
                        className="sr-only"
                        accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx"
                        onChange={(e) => handleFileChange(index, e.target.files?.[0] || null)}
                      />
                    </Label>
                  </div>
                  <Select
                    value={adjunto.categoria}
                    onValueChange={(val) => handleCategoriaChange(index, val)}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Seleccionar categoría..." />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIAS_PRESENTACION.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {CATEGORIAS_DOCUMENTO[cat]?.label ?? cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {adjunto.categoria === 'otros' && (
                    <Input
                      placeholder='Tipo de archivo (ej: "Salud Pública", "Constancia régimen X")'
                      value={adjunto.descripcion}
                      onChange={(e) => handleDescripcionChange(index, e.target.value)}
                      className="h-9"
                    />
                  )}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 mt-1"
                  onClick={() => handleQuitarAdjunto(index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirmar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
